// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import TimeSpent from 'app/common/lib/react/TimeSpent';
import Toast from 'app/common/react/ui/Toast';
import type { Dispatch } from 'redux';
import type { ProfileUser } from 'app/packages/pinterest-flow-types/user';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import {
  Avatar,
  Box,
  Button,
  Container,
  Masonry,
  MasonryUniformRowLayout,
  Spinner,
  Text,
} from 'gestalt';
import { follow, unfollow } from 'app/common/redux/actions/user/follow';

export type Props = {
  followers: {
    data: Array<ProfileUser>,
    fetchMore: () => void,
    isAtEnd: boolean,
    isFetching: boolean,
    isLoaded: boolean,
  },
  isOwnProfile: boolean,
  user: {
    data: {
      follower_count: number,
      username: string,
    },
    isLoaded: boolean,
  },
};

const getScrollContainer = () => window;
const COLUMN_WIDTH = 600;

type UserCardProps = {
  data: ProfileUser,
  followUser: (string) => void,
  hideAllToasts: () => void,
  isFollowed: boolean,
  showToast: (Toast: Object) => void,
  unfollowUser: (string) => void,
  viewer: Viewer,
};

export function UserCard({
  data: user,
  followUser,
  hideAllToasts,
  isFollowed,
  unfollowUser,
  viewer,
  showToast,
}: UserCardProps) {
  const buttonText = isFollowed
    ? i18n._('Unfollow', 'Button label to unfollow the creator of a pin on closeup')
    : i18n._('Follow', 'Button label to follow the creator of a pin on closeup');
  const buttonColor = isFollowed ? 'gray' : 'red';
  const userUrl = `/${user.username}/`;

  const onClick = isFollowed
    ? () => {
        unfollowUser(user.id);
      }
    : () => {
        followUser(user.id);
        hideAllToasts();
        showToast(({ onHide }) => (
          <Toast
            onClick={() => {
              startTiming(PCONST.thrift.TracingClientTypes.PWTAction.OTHER_PROFILE);
            }}
            onHide={onHide}
            text={[
              i18n._('Now following', 'text that comes before a board/user name in toast'),
              user.full_name,
            ]}
            href={userUrl}
            imageUrl={user.image_medium_url}
          />
        ));
      };
  const showFollowButton = viewer.isAuth && viewer.id !== user.id;
  const isVerified = user.verified_identity && user.verified_identity.verified;

  return (
    <Box display="flex" alignItems="center" justifyContent="between" paddingY={2}>
      <Box display="flex" alignItems="center">
        <Box width={72}>
          <NavigateLink
            key={`${userUrl}:avatar`}
            onClick={() => {
              startTiming(PCONST.thrift.TracingClientTypes.PWTAction.OTHER_PROFILE);
            }}
            to={userUrl}
          >
            <Avatar
              name={user.full_name}
              size="lg"
              src={user.image_xlarge_url}
              verified={isVerified}
            />
          </NavigateLink>
        </Box>
        <Box width={332} paddingX={4}>
          <NavigateLink
            key={`${userUrl}:name`}
            onClick={() => {
              startTiming(PCONST.thrift.TracingClientTypes.PWTAction.OTHER_PROFILE);
            }}
            to={userUrl}
          >
            <Text size="lg" weight="bold">
              {user.full_name}
            </Text>
          </NavigateLink>
        </Box>
      </Box>
      {showFollowButton && (
        <Box column={3} display="flex" justifyContent="center">
          <Box flex="shrink">
            <Button color={buttonColor} inline text={buttonText} onClick={onClick} size="sm" />
          </Box>
        </Box>
      )}
    </Box>
  );
}

const renderUser = compose(
  connect(
    (state, { isFollowed, data }) => {
      const user = state.users[data.id];
      return {
        isFollowed: user ? user.explicitly_followed_by_me : isFollowed,
        viewer: state.viewer,
      };
    },
    (dispatch: Dispatch<any>) => ({
      followUser: (id) => dispatch(follow(id)),
      unfollowUser: (id) => dispatch(unfollow(id)),
    }),
  ),
  withShowToast,
)(UserCard);

export default function ProfileFollowers({ user, followers, isOwnProfile }: Props) {
  const { data, fetchMore, isFetching, isLoaded } = followers;

  if (!user.isLoaded) {
    return null;
  }

  const timeSpentViewParam = isOwnProfile
    ? PCONST.thrift.ViewParameterType.USER_SELF
    : PCONST.thrift.ViewParameterType.USER_OTHERS;

  return (
    <TimeSpent
      view={PCONST.thrift.ViewType.USER}
      viewParameter={timeSpentViewParam}
      component={PCONST.thrift.ComponentType.USER_FOLLOWERS}
    >
      <Container>
        <Masonry
          columnWidth={COLUMN_WIDTH}
          comp={renderUser}
          gutterWidth={0}
          items={data || []}
          // $FlowFixMe: new errors found from flow 0.96 upgrade
          layout={MasonryUniformRowLayout}
          loadItems={fetchMore}
          minCols={1}
          scrollContainer={getScrollContainer}
          virtualize
        />
        {isLoaded &&
          data.length === 0 && (
            <Box marginBottom={3}>
              <Spinner
                accessibilityLabel={i18n._(
                  'Loading additional followers',
                  'Label for the loading state spinner in the People section of the Follower/Following page',
                )}
                show={isFetching || !isLoaded}
              />
            </Box>
          )}
      </Container>
    </TimeSpent>
  );
}
